import router from '@/router'
import type { RouteLocationNormalizedLoaded, RouteRecordRaw } from 'vue-router'
import { getRawRoute } from '@/utils/routerHelper'
import { defineStore } from 'pinia'
import { store } from '../index'
import { findIndex } from '@/utils'
import { useUserStoreWithOut } from './user'
import { securePiniaStorage } from '@/utils/securePinia'

export interface TagsViewState {
  visitedViews: RouteLocationNormalizedLoaded[]
  cachedViews: Set<string>
  cachedViewsMetadata: { [key: string]: { reset: boolean } }
  selectedTag?: RouteLocationNormalizedLoaded
}

export const useTagsViewStore = defineStore('tagsView', {
  state: (): TagsViewState => ({
    visitedViews: [],
    cachedViews: new Set(),
    cachedViewsMetadata: {},
    selectedTag: undefined
  }),
  getters: {
    getVisitedViews(): RouteLocationNormalizedLoaded[] {
      return this.visitedViews
    },
    getCachedViews(): string[] {
      return Array.from(this.cachedViews)
    },
    getSelectedTag(): RouteLocationNormalizedLoaded | undefined {
      return this.selectedTag
    }
  },
  actions: {
    // 新增緩存和tag
    addView(view: RouteLocationNormalizedLoaded): void {
      this.addVisitedView(view)
      this.addCachedView()
    },
    // 新增tag
    addVisitedView(view: RouteLocationNormalizedLoaded) {
      if (
        this.visitedViews.some((v) => v.path.toLocaleLowerCase() === view.path.toLocaleLowerCase())
      )
        return
      if (view.meta?.noTagsView) return
      this.visitedViews = [
        ...this.visitedViews, // 使用展開運算符創建一個新陣列
        Object.assign({}, view, { title: view.meta?.title || 'no-name' })
      ]
    },
    // 新增緩存
    addCachedView() {
      const cacheMap: Set<string> = new Set()
      for (const v of this.visitedViews) {
        const item = getRawRoute(v)
        const needCache = !item?.meta?.noCache
        if (!needCache) {
          continue
        }
        const name = item.name as string
        cacheMap.add(name)
      }
      if (Array.from(this.cachedViews).sort().toString() === Array.from(cacheMap).sort().toString())
        return
      this.cachedViews = cacheMap
    },
    // 删除某個
    delView(view: RouteLocationNormalizedLoaded) {
      this.delVisitedView(view)
      this.addCachedView()
    },
    // 删除tag
    delVisitedView(view: RouteLocationNormalizedLoaded) {
      for (const [i, v] of this.visitedViews.entries()) {
        if (v.path === view.path) {
          this.visitedViews.splice(i, 1)
          break
        }
      }
    },
    // 删除緩存
    delCachedView() {
      const route = router.currentRoute.value
      const index = findIndex<string>(this.getCachedViews, (v) => v === route.name)
      if (index > -1) {
        this.cachedViews.delete(this.getCachedViews[index])
      }
    },
    // 删除所有緩存和tag
    delAllViews() {
      this.delAllVisitedViews()
      this.addCachedView()
    },
    // 删除所有tag
    delAllVisitedViews() {
      const userStore = useUserStoreWithOut()

      // const affixTags = this.visitedViews.filter((tag) => tag.meta.affix)
      this.visitedViews = userStore.getUserInfo
        ? this.visitedViews.filter((tag) => tag?.meta?.affix)
        : []
    },
    // 删除其它
    delOthersViews(view: RouteLocationNormalizedLoaded) {
      this.delOthersVisitedViews(view)
      this.addCachedView()
    },
    // 删除其它tag
    delOthersVisitedViews(view: RouteLocationNormalizedLoaded) {
      this.visitedViews = this.visitedViews.filter((v) => {
        return v?.meta?.affix || v.path === view.path
      })
    },
    // 删除左側
    delLeftViews(view: RouteLocationNormalizedLoaded) {
      const index = findIndex<RouteLocationNormalizedLoaded>(
        this.visitedViews,
        (v) => v.path === view.path
      )
      if (index > -1) {
        this.visitedViews = this.visitedViews.filter((v, i) => {
          return v?.meta?.affix || v.path === view.path || i > index
        })
        this.addCachedView()
      }
    },
    // 删除右側
    delRightViews(view: RouteLocationNormalizedLoaded) {
      const index = findIndex<RouteLocationNormalizedLoaded>(
        this.visitedViews,
        (v) => v.path === view.path
      )
      if (index > -1) {
        this.visitedViews = this.visitedViews.filter((v, i) => {
          return v?.meta?.affix || v.path === view.path || i < index
        })
        this.addCachedView()
      }
    },
    updateVisitedView(view: RouteLocationNormalizedLoaded) {
      for (let v of this.visitedViews) {
        if (v.path === view.path) {
          v = Object.assign(v, view)
          break
        }
      }
    },
    // 設置當前選中的tag
    setSelectedTag(tag: RouteLocationNormalizedLoaded) {
      this.selectedTag = tag
    },
    setTitle(title: string, path?: string) {
      for (const v of this.visitedViews) {
        if (v.path === (path ?? this.selectedTag?.path)) {
          v.meta.title = title
          break
        }
      }
    },
    // 更新頁面緩存資料
    updateCachedViewMetadata(name: string, data: { reset?: boolean }) {
      this.cachedViewsMetadata[name] = {
        ...this.cachedViewsMetadata[name],
        reset: data.reset ?? this.cachedViewsMetadata[name]?.reset // 保持原值
      }
    },
    // 檢查頁面是否需要優先處理
    checkPageForInit(view: RouteLocationNormalizedLoaded) {
      const name = getRawRoute(view)?.name as string
      const metadata = this.cachedViewsMetadata[name]
      if (metadata) {
        // const temp = Object.assign({}, metadata)
        const temp = JSON.parse(JSON.stringify(metadata))
        if (metadata.reset) {
          // 設為已處理，防止再次執行
          this.cachedViewsMetadata[name].reset = false
        }
        return temp
      }
      return null
    },
    // 清除已刪除的 routes 對應的 tagsView
    clearTagsViewByRoutes(availableRoutes: RouteRecordRaw[]) {
      const availablePaths = new Set(
        availableRoutes.flatMap((route) => [
          route.path,
          ...(route.children?.map((child) => `${route.path}/${child.path}`) || [])
        ])
      )

      // 確保 `cachedViews` 是 Set 類型
      if (!(this.cachedViews instanceof Set)) {
        this.cachedViews = new Set(this.cachedViews)
      }

      // 移除 `visitedViews` 中不在 `availablePaths` 內的項目（使用 for 迴圈）
      for (let i = this.visitedViews.length - 1; i >= 0; i--) {
        if (!availablePaths.has(this.visitedViews[i].path)) {
          this.visitedViews.splice(i, 1)
        }
      }

      // `cachedViews` 轉換為陣列進行過濾，然後再轉回 Set
      for (const name of [...this.cachedViews]) {
        if (!availableRoutes.some((route) => route.name === name)) {
          this.cachedViews.delete(name)
        }
      }
    }
  },
  persist: [
    {
      pick: ['visitedViews'],
      key: `${securePiniaStorage.prefix('tagsView_visitedViews')}`,
      storage: securePiniaStorage.storage
    },
    {
      pick: ['cachedViews'],
      key: `${securePiniaStorage.prefix('tagsView_cachedViews')}`,
      storage: securePiniaStorage.storage
    },
    {
      pick: ['cachedViewsMetadata'],
      key: `${securePiniaStorage.prefix('tagsView_cachedViewsMetadata')}`,
      storage: securePiniaStorage.storage
    },
    {
      pick: ['selectedTag'],
      key: `${securePiniaStorage.prefix('tagsView_selectedTag')}`,
      storage: securePiniaStorage.storage
    }
  ]
})

export const useTagsViewStoreWithOut = () => {
  return useTagsViewStore(store)
}
